import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadCloud, Trash2, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";

function formatBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function iconFor(ct) {
  if (ct?.startsWith("image/")) return ImageIcon;
  if (ct?.includes("pdf") || ct?.startsWith("text/")) return FileText;
  return FileIcon;
}

export default function AdminFiles() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/files");
      setFiles(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large (max 50MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const onChange = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file? Codes linked to it will be unlinked.")) return;
    try {
      await api.delete(`/admin/files/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-files" className="space-y-8">
      <header>
        <p className="overline text-[var(--vault-text-muted)] mb-2">Cellar</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Files</h1>
        <p className="text-[var(--vault-text-muted)] mt-3 max-w-xl">
          Upload certificates of authenticity, tasting notes, provenance documents and label images. Once stored, link them to a bottle's access code.
        </p>
      </header>

      {/* Drop zone */}
      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-lg p-10 border-2 border-dashed text-center cursor-pointer transition-colors"
        style={{
          borderColor: dragOver ? "var(--vault-gold)" : "var(--vault-border)",
          background: dragOver ? "rgba(197,160,89,0.05)" : "var(--vault-surface)",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          onChange={onChange}
          className="hidden"
          data-testid="file-input"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.json"
        />
        <div className="flex flex-col items-center gap-3">
          <UploadCloud className="w-10 h-10" style={{ color: "var(--vault-gold)" }} />
          <p className="font-display text-xl">Drop a file or click to upload</p>
          <p className="text-sm text-[var(--vault-text-muted)]">PDF, image, text — up to 50MB</p>
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-[var(--vault-text-muted)] mt-2">
              <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
              Uploading…
            </div>
          )}
        </div>
      </div>

      {/* Files table */}
      <div
        className="rounded-lg overflow-hidden border"
        style={{ borderColor: "var(--vault-border)", background: "var(--vault-surface)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline text-[var(--vault-text-muted)]">
                <th className="px-4 py-4">File</th>
                <th className="px-4 py-4">Type</th>
                <th className="px-4 py-4">Size</th>
                <th className="px-4 py-4">Uploaded</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--vault-text-muted)]">Loading…</td></tr>
              )}
              {!loading && files.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-[var(--vault-text-muted)]">No files uploaded yet.</td></tr>
              )}
              {!loading && files.map((f) => {
                const Icon = iconFor(f.content_type);
                return (
                  <tr key={f.id} className="row border-t" style={{ borderColor: "var(--vault-border)" }} data-testid={`file-row-${f.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: "var(--vault-gold)" }} />
                        <span className="font-mono-vault text-xs sm:text-sm">{f.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-[var(--vault-text-muted)] text-xs">{f.content_type}</td>
                    <td className="px-4 py-4 text-[var(--vault-text-muted)]">{formatBytes(f.size)}</td>
                    <td className="px-4 py-4 text-[var(--vault-text-muted)] text-xs">{formatDate(f.created_at)}</td>
                    <td className="px-4 py-4 text-right">
                      <button onClick={() => remove(f.id)} className="p-2 rounded hover:bg-white/5" data-testid={`delete-file-${f.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400/80" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
